import api from 'config/api.config';
import { AxiosResponse } from 'axios';

// Define interfaces for support ticket data
export interface SupportTicket {
  _id: string;
  ticket_id?: string;
  user_id: string;
  category: 'technical_issue' | 'billing' | 'account' | 'feature_request' | 'bug_report' | 'general_inquiry' | 'other';
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'waiting_for_customer' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  recent_history?: TicketHistory[];
  history?: TicketHistory[];
  attachments?: any[];
  tags?: string[];
  metadata?: any;
  jira_issue_key?: string;
  jira_issue_id?: string;
}

export interface TicketHistory {
  _id: string;
  ticket_id: string;
  event_type: string;
  description: string;
  changed_by: {
    user_id: string;
    user_name?: string;
    user_email: string;
    source: string;
  };
  comment?: string;
  changes?: {
    field?: string;
    old_value?: any;
    new_value?: any;
  };
  jira_webhook_data?: {
    issue_key: string;
    issue_id: string;
  };
  metadata?: {
    ip_address?: string;
    user_agent?: string;
  };
  created_at: string;
}

export interface CreateTicketRequest {
  category: 'technical_issue' | 'billing' | 'account' | 'feature_request' | 'bug_report' | 'general_inquiry' | 'other';
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

export interface UpdateTicketRequest {
  status?: 'open' | 'in_progress' | 'waiting_for_customer' | 'resolved' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

export interface TicketFilters {
  status?: string;
  category?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface TicketStats {
  total_tickets: number;
  open_tickets: number;
  in_progress_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  avg_resolution_time?: number;
  by_category?: {
    [key: string]: number;
  };
  by_priority?: {
    [key: string]: number;
  };
}

export interface TicketsResponse {
  tickets: SupportTicket[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  filters?: {
    status: string;
    category: string;
  };
}

export interface JiraDetails {
  key: string;
  id: string;
  summary: string;
  status: string;
  priority: string;
  assignee: string | null;
  created: string;
  updated: string;
  resolved: string | null;
}

export interface SingleTicketResponse {
  ticket?: SupportTicket;
  data?: SupportTicket;
  history?: TicketHistory[];
  jira_details?: JiraDetails;
}

export interface StatsResponse {
  stats?: TicketStats;
  data?: TicketStats;
}

// Create a new support ticket
export const createSupportTicket = async (ticketData: CreateTicketRequest): Promise<SingleTicketResponse> => {
  try {
    const response: AxiosResponse<SingleTicketResponse> = await api.post(
      '/api/support/tickets',
      ticketData
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to create support ticket'
    );
  }
};

// List all tickets with optional filters
export const getSupportTickets = async (filters?: TicketFilters): Promise<TicketsResponse> => {
  try {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response: AxiosResponse<TicketsResponse> = await api.get(
      `/api/support/tickets${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch support tickets'
    );
  }
};

// Get a single ticket by ID with history
export const getSupportTicketById = async (ticketId: string): Promise<SingleTicketResponse> => {
  try {
    const response: AxiosResponse<SingleTicketResponse> = await api.get(
      `/api/support/tickets/${ticketId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch support ticket'
    );
  }
};

// Update ticket status or priority
export const updateSupportTicket = async (
  ticketId: string,
  updateData: UpdateTicketRequest
): Promise<SingleTicketResponse> => {
  try {
    const response: AxiosResponse<SingleTicketResponse> = await api.patch(
      `/api/support/tickets/${ticketId}`,
      updateData
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to update support ticket'
    );
  }
};

// Get ticket statistics
export const getSupportTicketStats = async (): Promise<StatsResponse> => {
  try {
    const response: AxiosResponse<StatsResponse> = await api.get(
      '/api/support/tickets/stats'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch ticket statistics'
    );
  }
};

// Add comment to ticket
export const addCommentToTicket = async (
  ticketId: string,
  comment: string
): Promise<any> => {
  try {
    const response = await api.post(
      `/api/support/tickets/${ticketId}/comments`,
      { comment }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to add comment'
    );
  }
};

// Upload attachment to ticket
export const uploadAttachmentToTicket = async (
  ticketId: string,
  file: File
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `/api/support/tickets/${ticketId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to upload attachment'
    );
  }
};
